import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOPageMeta from '@/components/feature/SEOPageMeta';
import { tvShows, categories } from '@/mocks/tvshows';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const show = tvShows.find((s) => s.id === id);

  if (!show) {
    return (
      <div className="w-full min-h-screen bg-background-50">
        <SEOPageMeta
          title="剧集未找到"
          description="您要查找的剧集可能已下架或链接有误，请返回首页浏览更多精彩内容。"
          keywords="久播影院,电视剧"
          canonicalUrl="https://www.jiubocinema.com/"
        />
        <div className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">剧集未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查找的剧集可能已下架或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const catInfo = categories.find((c) => c.slug === show.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${show.title} - 久播影院`,
    description: show.description,
    image: show.posterImage,
    datePublished: `${show.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '久播影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '久播影院',
    },
  };

  const relatedShows = tvShows
    .filter((s) => s.category === show.category && s.id !== show.id)
    .slice(0, 6);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <SEOPageMeta
        title={show.title}
        description={`${show.title} - ${show.description.slice(0, 120)}...高清全集免费在线观看，久播影院。`}
        keywords={`${show.title},${show.genres.join(',')},${show.region},久播影院`}
        canonicalUrl={`https://www.jiubocinema.com/article/${show.id}`}
        ogImage={show.posterImage}
      />
      <script type="application/ld+json">
        {JSON.stringify(articleJsonLd)}
      </script>
      <div className="pt-16">
        <div className="max-w-screen-2xl mx-auto">
          <Breadcrumb
            items={[
              { label: catInfo?.name || '分类', href: `/category/${show.category}` },
              { label: show.title },
            ]}
          />
        </div>

        <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${show.coverImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30"></div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 -mt-32 md:-mt-40 relative z-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-36 md:w-48 flex-shrink-0 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-lg">
                  <img
                    src={show.posterImage}
                    alt={show.title}
                    title={`${show.title} - 久播影院`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 pt-2">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
                  {show.title}
                </h1>
                {show.originalTitle && (
                  <p className="text-sm text-background-50/60 mb-3">{show.originalTitle}</p>
                )}

                <div className="flex items-center gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400 text-base"></i>
                    <span className="text-lg font-bold text-accent-400">{show.rating}</span>
                  </div>
                  <span className="text-background-50/40">|</span>
                  <span className="text-sm text-background-50/80">{show.year}</span>
                  <span className="text-background-50/40">|</span>
                  <span className="text-sm text-background-50/80">{show.region}</span>
                  <span className="text-background-50/40">|</span>
                  <span className="text-sm text-background-50/80">{show.episodes}集</span>
                  <span className="text-background-50/40">|</span>
                  <span className={`text-sm font-medium ${show.status === '更新中' ? 'text-accent-400' : 'text-green-400'}`}>
                    {show.status}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {show.genres.map((g) => (
                    <span
                      key={g}
                      className="px-2.5 py-1 text-xs border border-background-50/30 text-background-50/80 rounded-full whitespace-nowrap"
                    >
                      {g}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-3 flex-wrap mt-4">
                  <Link
                    to={`/category/${show.category}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-base"></i>
                    立即播放
                  </Link>
                  <button className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-background-50/40 text-background-50 text-sm rounded-lg hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line text-base"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-background-50/40 text-background-50 text-sm rounded-lg hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line text-base"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  剧情简介
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {show.description}
                </p>

                <div className="mt-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    演员阵容
                  </h2>
                  <div className="flex items-center gap-3 flex-wrap">
                    {show.cast.map((actor) => (
                      <div
                        key={actor}
                        className="flex items-center gap-2 bg-background-100 rounded-full px-4 py-2"
                      >
                        <div className="w-7 h-7 rounded-full bg-secondary-300 flex items-center justify-center">
                          <i className="ri-user-line text-xs text-foreground-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700 whitespace-nowrap">{actor}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    剧集列表
                  </h2>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {Array.from({ length: Math.min(show.episodes, 24) }, (_, i) => (
                      <button
                        key={i}
                        className="py-2 text-sm text-center bg-background-100 text-foreground-600 rounded-lg hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
                      >
                        第{i + 1}集
                      </button>
                    ))}
                    {show.episodes > 24 && (
                      <button className="py-2 text-sm text-center bg-background-100 text-foreground-400 rounded-lg cursor-pointer whitespace-nowrap">
                        ...
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">
                    剧集信息
                  </h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">导演</dt>
                      <dd className="text-sm text-foreground-800">{show.director}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">地区</dt>
                      <dd className="text-sm text-foreground-800">{show.region}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">年份</dt>
                      <dd className="text-sm text-foreground-800">{show.year}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">集数</dt>
                      <dd className="text-sm text-foreground-800">{show.episodes}集</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">状态</dt>
                      <dd className="text-sm text-foreground-800">{show.status}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">类型</dt>
                      <dd className="text-sm text-foreground-800">{show.genres.join(' / ')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-foreground-500">更新</dt>
                      <dd className="text-sm text-foreground-800">{show.updateInfo}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {relatedShows.length > 0 && (
              <div className="mt-12">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedShows.map((s) => (
                    <Link
                      key={s.id}
                      to={`/article/${s.id}`}
                      className="group"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5">
                        <img
                          src={s.posterImage}
                          alt={s.title}
                          title={`${s.title} - 久播影院`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-[10px] bg-primary-500 text-background-50 rounded whitespace-nowrap">
                            {s.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {s.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}